import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Shield, UserCircle, KeyRound, Loader2 } from 'lucide-react';

interface UserProfile {
    id: string;
    name: string;
    email: string;
    isSuperAdmin: boolean;
    isTwoFactorEnabled: boolean;
    isEmailVerified: boolean;
}

export default function Profile() {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    // Change password state
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const data = await apiFetch<{ user: UserProfile }>('/auth/me');
            setUser(data.user);
        } catch (error) {
            toast.error('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestCode = async () => {
        try {
            await apiFetch('/auth/change-password/request-code', { method: 'POST' });
            setCodeSent(true);
            toast.success('Verification code sent to your email');
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to send code');
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setChangingPassword(true);
        try {
            await apiFetch('/auth/change-password', {
                method: 'POST',
                body: JSON.stringify({ currentPassword, newPassword, mfaCode }),
            });
            toast.success('Password changed successfully');
            setShowChangePassword(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setMfaCode('');
            setCodeSent(false);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </div>
        );
    }

    if (!user) return null;

    return (
        <div className="min-h-screen flex flex-col bg-muted/30">
            <Navbar />

            <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
                <h1 className="text-3xl font-bold mb-8">Profile Settings</h1>

                <div className="grid gap-6">
                    {/* Personal Info */}
                    <Card className="p-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 rounded-full bg-primary/10 text-primary">
                                <UserCircle className="h-6 w-6" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold">Personal Information</h2>
                                <p className="text-sm text-muted-foreground">Manage your personal details</p>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label>Full Name</Label>
                                <div className="p-3 rounded-md bg-muted border border-border text-sm">
                                    {user.name}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Email Address</Label>
                                <div className="p-3 rounded-md bg-muted border border-border text-sm flex items-center justify-between">
                                    <span>{user.email}</span>
                                    {user.isEmailVerified ? (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Verified</span>
                                    ) : (
                                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">Unverified</span>
                                    )}
                                </div>
                            </div>
                            {user.isSuperAdmin && (
                                <div className="space-y-2">
                                    <Label>Account</Label>
                                    <div className="p-3 rounded-md bg-muted border border-border text-sm">
                                        Super Admin
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Security */}
                    <Card className="p-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 rounded-full bg-primary/10 text-primary">
                                <Shield className="h-6 w-6" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold">Security</h2>
                                <p className="text-sm text-muted-foreground">Manage your account security</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                                <div className="space-y-1">
                                    <Label className="text-base">Two-Factor Authentication (2FA)</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Email-based OTP verification is mandatory for all accounts.
                                    </p>
                                </div>
                                <Switch
                                    checked={user.isTwoFactorEnabled}
                                    disabled={true}
                                />
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                                <div className="space-y-1">
                                    <Label className="text-base">Change Password</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Update your password. Requires 2FA verification.
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowChangePassword(!showChangePassword)}
                                >
                                    <KeyRound className="mr-2 h-4 w-4" />
                                    {showChangePassword ? 'Cancel' : 'Change'}
                                </Button>
                            </div>

                            {showChangePassword && (
                                <form onSubmit={handleChangePassword} className="p-4 rounded-lg border border-border space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="currentPassword">Current Password</Label>
                                        <Input
                                            id="currentPassword"
                                            type="password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="newPassword">New Password</Label>
                                        <Input
                                            id="newPassword"
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Min 8 chars with uppercase, lowercase, number, and special character
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                        <Input
                                            id="confirmPassword"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="mfaCode">Verification Code</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                id="mfaCode"
                                                type="text"
                                                placeholder="123456"
                                                value={mfaCode}
                                                onChange={(e) => setMfaCode(e.target.value)}
                                                className="tracking-widest"
                                                maxLength={6}
                                                required
                                            />
                                            <Button type="button" variant="outline" onClick={handleRequestCode} disabled={codeSent}>
                                                {codeSent ? 'Sent' : 'Send Code'}
                                            </Button>
                                        </div>
                                    </div>
                                    <Button type="submit" className="w-full" disabled={changingPassword}>
                                        {changingPassword ? (
                                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Changing...</>
                                        ) : (
                                            'Change Password'
                                        )}
                                    </Button>
                                </form>
                            )}
                        </div>
                    </Card>
                </div>
            </main>
        </div>
    );
}
